import {
  availabilityGetBundlePriceSuccess,
  availabilitySearchAll,
  availabilitySearchAllHome,
  availabilitySearchByQueryParams,
  availabilitySearchFareAvailabilitySuccess,
  availabilitySearchFromDestinations,
  availabilitySellTripSelectionSuccess,
} from '@actions/availability.action';
import { Booking, CheckInPassengers } from '@models/booking.models';
import { createReducer, on } from '@ngrx/store';
import { Action } from 'ngx-bootstrap/mini-ngrx';
import { N0SsrAvailability } from '@models/responses/ssr-availability-response.model';
import {
  BookingActionTypes,
  bookingCancelFlightsSuccess,
  bookingCheckInFlights,
  bookingClearOriginalBooking,
  bookingDangerousGoodsViewed,
  bookingDeleteBookingSuccess,
  bookingDeleteBookingNoNavigation,
  bookingGetBooking,
  bookingGetBookingSuccess,
  bookingGetSsrAvailabilitySuccess,
  bookingPaymentsAcceptedBookingUpdated,
  bookingRetrieveBooking,
  bookingRetrieveBookingDeeplinkBags,
  bookingRetrieveBookingDeeplinkExtras,
  bookingRetrieveBookingDeeplinkMeals,
  bookingRetrieveBookingDeeplinkPay,
  bookingRetrieveBookingDeeplinkSeats,
  bookingRetrieveBookingNoNavigation,
  bookingRetrieveBookingSuccess,
  bookingSetOriginalBooking,
  bookingSetTokenGetBooking,
  bookingStoreBooking,
  bookingTravelerChange,
  bookingTripDetailsInit,
  bookingViewBoardingPass,
  tripDisruptionCheckSuccess,
  bookingRetrieveBookingError,
  bookingRetrieveTokenError,
  bookingGetBookingAfterPassengerUpdateSuccess,
  resetBookingError,
  setBookingError,
} from '@actions/booking.action';
import {
  N0CheckinDocCheckResponse, N0CheckinResponse,
  N0CheckinStatus
} from '@models/check-in.models';
import {
  checkInCheckInPassengersSuccess,
  checkInGetCheckInStatusSuccess,
  checkInCheckInPassengers, checkInDocCheckSuccess,
  checkInDocCheckReset
} from '@actions/check-in.action';
import { PlusgradeResponse } from '@models/responses/plusgrade-response.model';
import { plusgradeGetEligibilitySuccess } from '@actions/plusgrade.action';
import {
  coverGeniusSelectPolicy,
  coverGeniusSelectPolicySuccess,
} from '@actions/cover-genius.action';
import { updatePassengersSuccess } from '@actions/passenger.action';
import { extrasSellExtrasBundleSuccess } from '@actions/extras.action';

export interface State {
  booking: Booking | null;
  checkIn: N0CheckinResponse | null;
  checkInDocCheck: N0CheckinDocCheckResponse | null;
  checkInPassengers: CheckInPassengers | null;
  checkInStatus: N0CheckinStatus[] | null;
  currentlyActiveJourneyKey: string | null;
  dangerousGoodsViewed: { accepted: boolean } | null;
  disruption: DisruptionResponse | null;
  originalBooking: Booking | null;
  plusgrade: PlusgradeResponse | null;
  processing: boolean;
  ssrAvailability: N0SsrAvailability | null;
  tokenGetBooking: boolean;
  travelersToChange: CheckInPassengers | null;
  hasActiveBookingError: boolean;
}

export interface DisruptionResponse {
  disruptionUrl: string | undefined;
  isDisrupted: boolean;
}

export const INITIAL_STATE: State = {
  booking: null,
  originalBooking: null,
  currentlyActiveJourneyKey: null,
  ssrAvailability: null,
  checkInPassengers: null,
  travelersToChange: null,
  checkInStatus: null,
  checkIn: null,
  checkInDocCheck: null,
  dangerousGoodsViewed: null,
  plusgrade: null,
  processing: false,
  tokenGetBooking: false,
  disruption: null,
  hasActiveBookingError: false,
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    availabilitySearchAllHome,
    availabilitySearchAll,
    (state, action): State => ({
      ...state,
      booking: action.payload.clearBooking ? null : state.booking,
      plusgrade: action.payload.clearBooking ? null : state.plusgrade,
      tokenGetBooking: action.payload.clearBooking
        ? false
        : state.tokenGetBooking,
    }),
  ),

  on(
    availabilitySearchFareAvailabilitySuccess,
    (state, action): State => ({
      ...state,
      booking: action.clearBooking ? null : state.booking,
      plusgrade: action.clearBooking ? null : state.plusgrade,
      tokenGetBooking: action.clearBooking ? false : state.tokenGetBooking,
    }),
  ),

  on(
    bookingStoreBooking,
    availabilitySellTripSelectionSuccess,
    coverGeniusSelectPolicySuccess,
    extrasSellExtrasBundleSuccess,
    (state, action): State => ({
      ...state,
      booking: action.payload,
      processing: false,
    }),
  ),

  on(
    bookingStoreBooking,
    (state, action): State => ({
      ...state,
      originalBooking: action.payload,
    }),
  ),

  on(
    bookingGetBooking,
    bookingRetrieveBooking,
    bookingRetrieveBookingNoNavigation,
    bookingRetrieveBookingDeeplinkBags,
    bookingRetrieveBookingDeeplinkSeats,
    bookingRetrieveBookingDeeplinkMeals,
    bookingRetrieveBookingDeeplinkExtras,
    bookingRetrieveBookingDeeplinkPay,
    coverGeniusSelectPolicy,
    updatePassengersSuccess,
    (state): State => {
      return {
        ...state,
        processing: true,
        tokenGetBooking: false,
      };
    },
  ),

  on(
    bookingGetBookingSuccess,
    bookingGetBookingAfterPassengerUpdateSuccess,
    bookingRetrieveBookingSuccess,
    bookingPaymentsAcceptedBookingUpdated,
    bookingCancelFlightsSuccess,
    (state, action): State => {
      const isCancelled = action.booking.journeys.length <= 0;
      const hasRetrievedBooking = state.originalBooking != null;

      if (
        !isCancelled ||
        !hasRetrievedBooking ||
        action.type === BookingActionTypes.retrieveBookingSuccess
      ) {
        // if not in cancel flow, or have no retrieved
        // booking to maintain, copy resultant
        // booking into the booking node as normal.
        // always copy retrieved booking into booking node
        return {
          ...state,
          booking: action.booking,
          processing: false,
        };
      }

      // cancelled booking in state with retrieved booking in store.
      // persist information about the journeys, bags, etc. for cancel
      // and cancel confirmation pages.
      const {
        addOns,
        bags,
        breakdown,
        details,
        extras,
        journeys,
        meals,
        other,
        passengers,
        seats,
        tripBreakdown,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } = state.originalBooking!;

      return {
        ...state,
        booking: {
          ...action.booking,
          addOns,
          bags,
          breakdown: {
            ...breakdown,
            balanceDue: action.booking.breakdown.balanceDue,
          },
          details,
          extras,
          journeys,
          meals,
          other,
          passengers,
          seats,
          tripBreakdown: {
            ...tripBreakdown,
            balanceDue: action.booking.tripBreakdown.balanceDue,
          },
        },
        processing: false,
      };
    },
  ),

  // Loading a new booking into state, or starting a fresh check-in flow
  on(
    bookingRetrieveBookingSuccess,
    bookingCheckInFlights,
    (state): State => ({
      ...state,
      dangerousGoodsViewed: null,
    }),
  ),

  on(
    bookingRetrieveTokenError,
    bookingRetrieveBookingError,
    (state): State => ({
      ...state,
      processing: false,
    }),
  ),

  on(
    bookingSetOriginalBooking,
    bookingRetrieveBookingSuccess,
    (state, action): State => ({
      ...state,
      originalBooking: action.booking,
    }),
  ),

  on(
    bookingClearOriginalBooking,
    (state): State => ({
      ...state,
      originalBooking: null,
    }),
  ),

  on(
    bookingDeleteBookingSuccess,
    (state): State => ({
      ...state,
      booking: null,
      plusgrade: null,
    }),
  ),

  on(
    bookingDeleteBookingNoNavigation,
    (state): State => ({
      ...state,
      booking: null,
    }),
  ),

  on(
    bookingGetSsrAvailabilitySuccess,
    (state, action): State => ({
      ...state,
      ssrAvailability: action.payload,
    }),
  ),

  on(
    availabilitySellTripSelectionSuccess,
    (state, action): State => ({
      ...state,
      ssrAvailability: action.isUpgraded ? state.ssrAvailability : null,
    }),
  ),

  on(
    bookingRetrieveBookingSuccess,
    bookingStoreBooking,
    (state): State => ({
      ...state,
      ssrAvailability: null,
    }),
  ),

  on(
    bookingTravelerChange,
    (state, action): State => ({
      ...state,
      travelersToChange: action.travelersToChange,
    }),
  ),

  on(
    bookingCheckInFlights,
    bookingViewBoardingPass,
    (state, action): State => ({
      ...state,
      checkInPassengers: action.checkInPassengers,
    }),
  ),

  on(
    bookingTripDetailsInit,
    (state): State => ({
      ...state,
      checkInPassengers: null,
      travelersToChange: null,
    }),
  ),

  on(
    checkInGetCheckInStatusSuccess,
    (state, action): State => ({
      ...state,
      checkInStatus: action.payload,
    }),
  ),

  on(
    checkInCheckInPassengersSuccess,
    (state, action): State => ({
      ...state,
      checkIn: action.payload,
    }),
  ),

  on(
    bookingDangerousGoodsViewed,
    (state, action): State => ({
      ...state,
      dangerousGoodsViewed: action.payload,
    }),
  ),

  on(
    checkInCheckInPassengers,
    (state): State => ({
      ...state,
      dangerousGoodsViewed: { accepted: true },
    }),
  ),

  on(
    plusgradeGetEligibilitySuccess,
    (state, action): State => ({
      ...state,
      plusgrade: action.payload,
    }),
  ),

  on(
    bookingSetTokenGetBooking,
    (state, action): State => ({
      ...state,
      tokenGetBooking: action.getBooking,
    }),
  ),

  on(
    availabilitySearchByQueryParams,
    availabilitySearchFromDestinations,
    (state): State => ({
      ...state,
      ssrAvailability: null,
      tokenGetBooking: false,
      booking: null,
    }),
  ),

  on(
    tripDisruptionCheckSuccess,
    (state, action): State => ({
      ...state,
      disruption: {
        isDisrupted: action.isDisrupted,
        disruptionUrl: action.disruptionUrl,
      },
    }),
  ),

  on(availabilityGetBundlePriceSuccess, (state, action): State => {
    if (!state.ssrAvailability) {
      return state;
    }

    let updatedJourneySsrs = state.ssrAvailability.journeySsrs;

    if (action.price > 0) {
      updatedJourneySsrs = updatedJourneySsrs.map((journeySsr) => {
        return {
          ...journeySsr,
          bundlePrice: action.price,
        };
      });
    }

    return {
      ...state,
      ssrAvailability: {
        ...state.ssrAvailability,
        journeySsrs: updatedJourneySsrs,
      },
    };
  }),

  on(
    checkInDocCheckSuccess,
    (state, action): State => ({
      ...state,
      checkInDocCheck: action.payload,
    }),
  ),

  on(
    checkInDocCheckReset,
    (state): State => ({
      ...state,
      checkInDocCheck: null,
    }),
  ),

  on(
    setBookingError,
    (state): State => ({
      ...state,
      hasActiveBookingError: true,
    }),
  ),

  on(resetBookingError,
    (state): State => ({
      ...state,
      hasActiveBookingError: false,
    }),
  ),
);

export const bookingReducer = (state: State, action: Action): State =>
  reducer(state, action);
